/* eslint-disable react-refresh/only-export-components -- router module exports the router instance alongside internal wrapper components. */
import { lazy, Suspense, useEffect, type ComponentType, type ReactNode } from 'react'
import { Navigate, createBrowserRouter, useLocation, useRouteError, type RouteObject } from 'react-router'
import { AppShell } from '@/components/layout/AppShell'
import { PermissionGate, PlatformStaffGate } from '@/components/shared/PermissionGate'
// Not lazy: the store gate is tiny and must never flash the storefront while loading.
import { StoreGate } from '@/components/storefront/StoreGate'
import { MANAGE_CLINIC, MANAGE_ORG, MANAGE_NETWORK, MANAGE_PLATFORM, REPORTS } from '@/auth/permissions'
import { captureHandledError } from '@/sentry'
import { onBookingHost } from '@/lib/booking-host'

// Auto-reload once on chunk load failure (stale cache after deploy).
// Uses a timestamp so the flag expires after 30 seconds — avoids staying
// stuck if the user hard-refreshes during a loop.
// Mirror React.lazy's loose typing — components accept varied props.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function lazyWithReload(importFn: () => Promise<{ default: ComponentType<any> }>) {
  return lazy(() =>
    importFn()
      .then((mod) => {
        // Successful load — clear any reload flag
        sessionStorage.removeItem('claro:chunk-reload')
        return mod
      })
      .catch((err) => {
        const key = 'claro:chunk-reload'
        const last = sessionStorage.getItem(key)
        const now = Date.now()
        // Only reload if we haven't tried in the last 10 seconds
        if (!last || now - Number(last) > 10000) {
          sessionStorage.setItem(key, String(now))
          window.location.reload()
          return new Promise(() => {})
        }
        throw err
      })
  )
}

const NotesView = lazyWithReload(() => import('@/components/notes/NotesView'))
const HelpPage = lazyWithReload(() => import('@/components/help/HelpPage'))
const HelpArticlePage = lazyWithReload(() => import('@/components/help/HelpArticlePage'))
const CalendarView = lazyWithReload(() => import('@/components/calendar/CalendarView'))
const BookingConfirmationsView = lazyWithReload(() => import('@/components/bookings/BookingConfirmationsView'))
const BookingCalendarView = lazyWithReload(() => import('@/components/bookings/BookingCalendarView'))
const PatientsView = lazyWithReload(() => import('@/components/patients/PatientsView'))
const PatientDetail = lazyWithReload(() => import('@/components/patients/PatientDetail'))
const PatientImportView = lazyWithReload(() => import('@/components/patients/import/PatientImportView'))
const HistoryImportView = lazyWithReload(() => import('@/components/patients/import/history/HistoryImportView'))
const TimelyMoveView = lazyWithReload(() => import('@/components/patients/import/move/TimelyMoveView'))
const SettingsView = lazyWithReload(() => import('@/components/settings/SettingsView'))
const ManageLanding = lazyWithReload(() => import('@/components/manage/ManageLanding'))
const UsersList = lazyWithReload(() => import('@/components/admin/users/UsersList'))
const UserCreate = lazyWithReload(() => import('@/components/admin/users/UserCreate'))
const UserInvite = lazyWithReload(() => import('@/components/admin/users/UserInvite'))
const PendingInvites = lazyWithReload(() => import('@/components/manage/PendingInvites'))
const AccessRequests = lazyWithReload(() => import('@/components/manage/AccessRequests'))
const StoreOnboardingQueue = lazyWithReload(() => import('@/components/manage/StoreOnboardingQueue'))
const Subscriptions = lazyWithReload(() => import('@/components/manage/Subscriptions'))
const AdminEmails = lazyWithReload(() => import('@/components/manage/Emails'))
const UserDetail = lazyWithReload(() => import('@/components/admin/users/UserDetail'))
const Adoption = lazyWithReload(() => import('@/components/admin/adoption/Adoption'))
const FeedbackConsole = lazyWithReload(() => import('@/components/admin/feedback/FeedbackConsole'))
const FeedbackCaseView = lazyWithReload(() => import('@/components/admin/feedback/FeedbackCaseView'))
const OrgsList = lazyWithReload(() => import('@/components/admin/orgs/OrgsList'))
const ClinicDetail = lazyWithReload(() => import('@/components/admin/orgs/ClinicDetail'))
const OrgDetail = lazyWithReload(() => import('@/components/admin/orgs/OrgDetail'))
const TreatmentsList = lazyWithReload(() => import('@/components/admin/clinical/TreatmentsList'))
const TreatmentAreasList = lazyWithReload(() => import('@/components/admin/clinical/TreatmentAreasList'))
const ConsultationTypes = lazyWithReload(() => import('@/components/admin/clinical/ConsultationTypes'))
const Specialties = lazyWithReload(() => import('@/components/admin/clinical/Specialties'))
const LabelCatalogue = lazyWithReload(() => import('@/components/admin/clinical/LabelCatalogue'))
const MedicinesCatalogue = lazyWithReload(() => import('@/components/admin/clinical/MedicinesCatalogue'))
const ArtgWorklist = lazyWithReload(() => import('@/components/admin/clinical/ArtgWorklist'))
const MedicineDetail = lazyWithReload(() => import('@/components/admin/clinical/MedicinesCatalogue').then(m => ({ default: m.MedicineDetail })))
const PharmaCompanies = lazyWithReload(() => import('@/components/admin/clinical/PharmaCompanies'))
const PharmaCompanyDetail = lazyWithReload(() => import('@/components/admin/clinical/PharmaCompanies').then(m => ({ default: m.PharmaCompanyDetail })))
const DistributorsList = lazyWithReload(() => import('@/components/admin/clinical/Distributors'))
const DistributorDetail = lazyWithReload(() => import('@/components/admin/clinical/Distributors').then(m => ({ default: m.DistributorDetail })))
const InventoryDashboard = lazyWithReload(() => import('@/components/manage/inventory/InventoryDashboard'))
const IntelligenceDashboard = lazyWithReload(() => import('@/components/manage/inventory/IntelligenceDashboard'))
const InventoryLocations = lazyWithReload(() => import('@/components/manage/inventory/InventoryLocations'))
const ReceiveStock = lazyWithReload(() => import('@/components/manage/inventory/ReceiveStock'))
const TransactionHistory = lazyWithReload(() => import('@/components/manage/inventory/TransactionHistory'))
const BatchTrace = lazyWithReload(() => import('@/components/manage/inventory/BatchTrace'))
const ExpiryDashboard = lazyWithReload(() => import('@/components/manage/inventory/ExpiryDashboard'))
const Stocktake = lazyWithReload(() => import('@/components/manage/inventory/Stocktake'))
const DrugRegister = lazyWithReload(() => import('@/components/manage/inventory/DrugRegister'))
const BatchRecall = lazyWithReload(() => import('@/components/manage/inventory/BatchRecall'))
const InventoryPeriods = lazyWithReload(() => import('@/components/manage/inventory/InventoryPeriods'))
const VialTracker = lazyWithReload(() => import('@/components/manage/inventory/VialTracker'))
const DeviceRegister = lazyWithReload(() => import('@/components/manage/inventory/DeviceRegister'))
const ConsentFormsList = lazyWithReload(() => import('@/components/admin/clinical/ConsentFormsList'))
const ConsentFormDetail = lazyWithReload(() => import('@/components/admin/clinical/ConsentFormDetail'))
const AiUsageDashboard = lazyWithReload(() => import('@/components/admin/ai/AiUsageDashboard'))
const EvalDashboard = lazyWithReload(() => import('@/components/admin/ai/EvalDashboard'))
const ComparisonsPanel = lazyWithReload(() => import('@/components/admin/ai/ComparisonsPanel'))
const AiRoutingPanel = lazyWithReload(() => import('@/components/admin/ai/AiRoutingPanel'))
const TranscriptionControlPanel = lazyWithReload(
  () => import('@/components/admin/ai/TranscriptionControlPanel'),
)
const AiModelManagement = lazyWithReload(() => import('@/components/admin/ai/AiModelManagement'))
const PromptEditor = lazyWithReload(() => import('@/components/admin/ai/PromptEditor'))
const AdminPatients = lazyWithReload(() => import('@/components/admin/AdminPatients'))
const EmrSyncDashboard = lazyWithReload(() => import('@/components/admin/EmrSyncDashboard'))
const PatientDuplicates = lazyWithReload(() => import('@/components/admin/data/PatientDuplicates'))
const PatientImport = lazyWithReload(() => import('@/components/admin/data/PatientImport'))
const PlatformSettings = lazyWithReload(() => import('@/components/admin/platform/PlatformSettings'))
const CompliancePlatformSettings = lazyWithReload(() => import('@/components/admin/platform/CompliancePlatformSettings'))
const DangerZone = lazyWithReload(() => import('@/components/admin/platform/DangerZone'))
const FeatureFlagsAdmin = lazyWithReload(() => import('@/components/admin/FeatureFlagsAdmin'))
const PlatformStatusAdmin = lazyWithReload(() => import('@/components/admin/platform/PlatformStatusAdmin'))
const HelpLibrary = lazyWithReload(() => import('@/components/admin/platform/HelpLibrary'))
const CalendarAdmin = lazyWithReload(() => import('@/components/admin/calendar/CalendarAdmin'))
const AcuityMoveView = lazyWithReload(() => import('@/components/manage/clinic/acuity/AcuityMoveView'))
const CalendarAnalytics = lazyWithReload(() => import('@/components/admin/calendar/CalendarAnalytics'))
const ReportsPage = lazyWithReload(() => import('@/components/reports/ReportsPage'))
const InboxView = lazyWithReload(() => import('@/components/inbox/InboxView'))
const TasksView = lazyWithReload(() => import('@/components/tasks/TasksView'))
const ReviewTodayPage = lazyWithReload(() => import('@/components/tasks/ReviewTodayPage'))
const ManageLayout = lazyWithReload(() => import('@/components/manage/ManageLayout'))
const ClinicOverview = lazyWithReload(() => import('@/components/manage/clinic/ClinicOverview'))
const ClinicStaff = lazyWithReload(() => import('@/components/manage/clinic/ClinicStaff'))
const ClinicSettingsPage = lazyWithReload(() => import('@/components/manage/clinic/ClinicSettings'))
const ClinicBooking = lazyWithReload(() => import('@/components/manage/clinic/ClinicBooking'))
const ClinicSales = lazyWithReload(() => import('@/components/manage/clinic/ClinicSales'))
const ClinicGiftCards = lazyWithReload(() => import('@/components/manage/clinic/ClinicGiftCards'))
const ClinicDiscardedNotes = lazyWithReload(() => import('@/components/manage/clinic/ClinicDiscardedNotes'))
const ClinicPackages = lazyWithReload(() => import('@/components/manage/clinic/ClinicPackages'))
const ClinicBranding = lazyWithReload(() => import('@/components/manage/clinic/ClinicBranding'))
const ClinicMessages = lazyWithReload(() => import('@/components/manage/clinic/ClinicMessages'))
const ClinicEncounterDefaults = lazyWithReload(() => import('@/components/manage/clinic/ClinicEncounterDefaults'))
const ClinicIntegrations = lazyWithReload(() => import('@/components/manage/clinic/ClinicIntegrations'))
const ClinicHalaxySettings = lazyWithReload(() => import('@/components/manage/clinic/halaxy/ClinicHalaxySettings'))
const ClinicHalaxySyncLog = lazyWithReload(() => import('@/components/manage/clinic/halaxy/ClinicHalaxySyncLog'))
const ClinicExport = lazyWithReload(() => import('@/components/manage/clinic/ClinicExport'))
const ApiManagement = lazyWithReload(() => import('@/components/admin/ApiManagement'))
const OrgMembers = lazyWithReload(() => import('@/components/manage/org/OrgMembers'))
const OrgSettingsPage = lazyWithReload(() => import('@/components/manage/org/OrgSettings'))
const NetworkSettingsPage = lazyWithReload(() => import('@/components/manage/network/NetworkSettings'))
const ClinicOrders = lazyWithReload(() => import('@/components/manage/clinic/ClinicOrders'))
const CreateOrder = lazyWithReload(() => import('@/components/manage/clinic/CreateOrder'))
const OrderDetail = lazyWithReload(() => import('@/components/manage/clinic/OrderDetail'))
const ConfirmDeliveryPage = lazyWithReload(() => import('@/components/manage/clinic/ConfirmDelivery'))
const NetworkOrders = lazyWithReload(() => import('@/components/manage/network/NetworkOrders'))
const NetworkOrderDetail = lazyWithReload(() => import('@/components/manage/network/NetworkOrderDetail'))
const WholesalePricingManager = lazyWithReload(() => import('@/components/manage/network/WholesalePricingManager'))
const FormularyManager = lazyWithReload(() => import('@/components/manage/network/FormularyManager'))
const WholesaleAccountsManager = lazyWithReload(() => import('@/components/manage/network/WholesaleAccountsManager'))
const CredentialsManager = lazyWithReload(() => import('@/components/manage/network/CredentialsManager'))
const ComplianceDashboard = lazyWithReload(() => import('@/components/manage/network/ComplianceDashboard'))
const IncidentsList = lazyWithReload(() => import('@/components/safety/IncidentsList'))
const IncidentDetail = lazyWithReload(() => import('@/components/safety/IncidentDetail'))
const IncidentReportForm = lazyWithReload(() => import('@/components/safety/IncidentReportForm'))
const MdInboxView = lazyWithReload(() => import('@/components/safety/MdInboxView'))
const NetworkRollupView = lazyWithReload(
  () => import('@/components/safety/NetworkRollupView'),
)
const SafetyLayout = lazyWithReload(() => import('@/components/safety/SafetyLayout'))
const AuditorGrants = lazyWithReload(() => import('@/components/safety/AuditorGrants'))
const RegulatorReportsList = lazyWithReload(() => import('@/components/safety/RegulatorReportsList'))
const RegulatorReportDetail = lazyWithReload(() => import('@/components/safety/RegulatorReportDetail'))
const InvoiceDashboard = lazyWithReload(() => import('@/components/payments/InvoiceDashboard'))
const InvoiceDetail = lazyWithReload(() => import('@/components/payments/InvoiceDetail'))
const PaymentTierManager = lazyWithReload(() => import('@/components/payments/PaymentTierManager'))
const ClinicPaymentConfig = lazyWithReload(() => import('@/components/payments/ClinicPaymentConfig'))
const PaymentMethodSetup = lazyWithReload(() => import('@/components/payments/PaymentMethodSetup'))
const StoreLayout = lazyWithReload(() => import('@/components/storefront/StoreLayout'))
const StoreHome = lazyWithReload(() => import('@/components/storefront/StoreHome'))
const StoreBrowse = lazyWithReload(() => import('@/components/storefront/StoreBrowse'))
const ProductDetailPage = lazyWithReload(() => import('@/components/storefront/ProductDetailPage'))
const BrandPage = lazyWithReload(() => import('@/components/storefront/BrandPage'))
const CategoryPage = lazyWithReload(() => import('@/components/storefront/CategoryPage'))
const SearchResults = lazyWithReload(() => import('@/components/storefront/SearchResults'))
const FavouritesPage = lazyWithReload(() => import('@/components/storefront/FavouritesPage'))
const StandingOrdersPage = lazyWithReload(() => import('@/components/storefront/StandingOrdersPage'))
const MessagingView = lazyWithReload(() => import('@/components/messaging/MessagingView'))
const ConsentSigningPage = lazyWithReload(() => import('@/components/consent-signing/ConsentSigningPage'))
const ForgotPasswordPage = lazyWithReload(() => import('@/components/auth/ForgotPasswordPage'))
const ResetPasswordPage = lazyWithReload(() => import('@/components/auth/ResetPasswordPage'))
const SignupPage = lazyWithReload(() => import('@/components/auth/SignupPage'))
const NotFound = lazyWithReload(() => import('@/components/layout/NotFound'))

// Standalone clinic-owner onboarding wizard (CLA-520)
const OnboardingWelcome = lazyWithReload(() => import('@/components/onboarding/Welcome'))
const OnboardingPractitionerIdentity = lazyWithReload(
  () => import('@/components/onboarding/PractitionerIdentity'),
)
const OnboardingPasskey = lazyWithReload(() => import('@/components/onboarding/PasskeyStep'))
const OnboardingFirstClinic = lazyWithReload(() => import('@/components/onboarding/FirstClinic'))
const OnboardingAnotherClinic = lazyWithReload(
  () => import('@/components/onboarding/AnotherClinic'),
)
const OnboardingDocuments = lazyWithReload(() => import('@/components/onboarding/Documents'))
const OnboardingCompleteProfile = lazyWithReload(
  () => import('@/components/onboarding/CompleteProfile'),
)

// Patient Portal
const PortalBrandingProvider = lazyWithReload(() => import('@/components/portal/BrandingProvider'))
const PortalLayout = lazyWithReload(() => import('@/components/portal/PortalLayout'))
const PortalLogin = lazyWithReload(() => import('@/components/portal/PatientLogin'))
const PortalRegister = lazyWithReload(() => import('@/components/portal/PatientRegister'))
const PortalActivate = lazyWithReload(() => import('@/components/portal/PatientActivate'))
const PortalMagicLinkVerify = lazyWithReload(() => import('@/components/portal/PatientMagicLinkVerify'))
const PortalHandoff = lazyWithReload(() => import('@/components/portal/PatientHandoff'))
const PortalSlugClinic = lazyWithReload(() => import('@/components/portal/PortalSlugClinic'))
const PatientVisit = lazyWithReload(() => import('@/components/portal/PatientVisit'))
const PatientStoreHome = lazyWithReload(() => import('@/components/portal/PatientStoreHome'))
const PatientProductDetail = lazyWithReload(() => import('@/components/portal/PatientProductDetail'))
const PatientCart = lazyWithReload(() => import('@/components/portal/PatientCart'))
const PortalCheckout = lazyWithReload(() => import('@/components/portal/CheckoutPage'))
const PatientOrders = lazyWithReload(() => import('@/components/portal/PatientOrders'))
const PatientOrderDetail = lazyWithReload(() => import('@/components/portal/PatientOrderDetail'))
const PatientBook = lazyWithReload(() => import('@/components/portal/PatientBook'))
const PatientBookings = lazyWithReload(() => import('@/components/portal/PatientBookings'))
const PatientProfile = lazyWithReload(() => import('@/components/portal/PatientProfile'))
const PatientDocuments = lazyWithReload(() => import('@/components/portal/PatientDocuments'))
const PatientPlan = lazyWithReload(() => import('@/components/portal/PatientPlan'))
const PatientGiftCards = lazyWithReload(() => import('@/components/portal/PatientGiftCards'))
const PatientGiftCardBuy = lazyWithReload(() => import('@/components/portal/PatientGiftCardBuy'))
const PublicBook = lazyWithReload(() => import('@/components/public/PublicBook'))
const ManageBooking = lazyWithReload(() => import('@/components/public/ManageBooking'))
const PublicBookingClaim = lazyWithReload(() => import('@/components/public/PublicBookingClaim'))

function LazyFallback() {
  return (
    <div className="flex items-center justify-center flex-1 w-full">
      <div className="w-5 h-5 border-2 border-claro-200 border-t-claro-600 rounded-full animate-spin" />
    </div>
  )
}

function SuspenseWrapper({ children }: { children: ReactNode }) {
  return <Suspense fallback={<LazyFallback />}>{children}</Suspense>
}

// Placeholder for views not yet built
function ComingSoon({ name }: { name: string }) {
  return (
    <div className="flex-1 w-full flex items-center justify-center">
      <div className="text-center">
        <p className="text-sm text-ink-tertiary">{name}</p>
        <p className="text-xs text-ink-faint mt-1">Coming soon</p>
      </div>
    </div>
  )
}

function RouteErrorFallback() {
  const error = useRouteError()
  useEffect(() => {
    // React Router catches loader/render errors before they reach the SDK's
    // global handlers; report genuine faults (CLA-694). Expected route
    // Responses (e.g. 404) aren't Errors, so captureHandledError skips them.
    captureHandledError(error, { source: 'react-router' })
  }, [error])
  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="text-center space-y-3 max-w-sm">
        <p className="text-sm font-medium text-ink">Something went wrong</p>
        <p className="text-xs text-ink-faint">An unexpected error occurred while loading this page.</p>
        <button
          onClick={() => window.location.reload()}
          className="px-3 py-1.5 text-xs font-medium text-claro-600 border border-claro-200 rounded-lg hover:bg-claro-50 transition-colors"
        >
          Reload page
        </button>
      </div>
    </div>
  )
}

/** `/portal` → the patient's documents, keeping `?clinic_id` and the rest of the query. */
function PortalCareHome() {
  const { search, hash } = useLocation()
  return <Navigate to={`/portal/documents${search}${hash}`} replace />
}

const appRoutes: RouteObject[] = [
  {
    element: <AppShell />,
    errorElement: <RouteErrorFallback />,
    children: [
      // ── Standalone owner onboarding (CLA-520) — nested inside AppShell so
      // auth state is initialised, but AppShell renders these without the
      // sidebar/chrome via a path-based check.
      {
        path: '/onboarding/welcome',
        element: <SuspenseWrapper><OnboardingWelcome /></SuspenseWrapper>,
      },
      {
        path: '/onboarding/practitioner-identity',
        element: <SuspenseWrapper><OnboardingPractitionerIdentity /></SuspenseWrapper>,
      },
      {
        path: '/onboarding/passkey',
        element: <SuspenseWrapper><OnboardingPasskey /></SuspenseWrapper>,
      },
      {
        path: '/onboarding/clinic',
        element: <SuspenseWrapper><OnboardingFirstClinic /></SuspenseWrapper>,
      },
      {
        path: '/onboarding/documents',
        element: <SuspenseWrapper><OnboardingDocuments /></SuspenseWrapper>,
      },
      {
        path: '/onboarding/another-clinic',
        element: <SuspenseWrapper><OnboardingAnotherClinic /></SuspenseWrapper>,
      },
      {
        // CLA-711 — owner-facing "complete your clinic profile" flow. Under
        // /onboarding/* so AppShell renders it chrome-free; it is intentionally
        // NOT a first-run wizard path, so onboarded users aren't redirected away.
        path: '/onboarding/complete-profile',
        element: <SuspenseWrapper><OnboardingCompleteProfile /></SuspenseWrapper>,
      },
      {
        path: '/',
        element: (
          <SuspenseWrapper>
            <NotesView />
          </SuspenseWrapper>
        ),
      },
      {
        path: '/calendar',
        element: (
          <SuspenseWrapper>
            <CalendarView />
          </SuspenseWrapper>
        ),
      },
      {
        path: '/confirmations',
        element: (
          <SuspenseWrapper>
            <BookingConfirmationsView />
          </SuspenseWrapper>
        ),
      },
      {
        path: '/bookings/calendar',
        element: (
          <SuspenseWrapper>
            <BookingCalendarView />
          </SuspenseWrapper>
        ),
      },
      {
        path: '/patients',
        element: (
          <SuspenseWrapper>
            <PatientsView />
          </SuspenseWrapper>
        ),
      },
      {
        // CLA-1968 "Move from Timely": every report at once, in Claro's order.
        // A static segment, so it ranks above `:importId` below. The URL
        // carries nothing: the move is the caller's current one.
        path: '/patients/import/timely',
        element: (
          <SuspenseWrapper>
            <TimelyMoveView />
          </SuspenseWrapper>
        ),
      },
      {
        // CLA-1714 Timely notes and appointment history, next to the patient
        // list import. A static segment, so it ranks above `:importId` below.
        // Gated inside the view; the URL carries only the import's UUID.
        path: '/patients/import/notes/:importId?',
        element: (
          <SuspenseWrapper>
            <HistoryImportView />
          </SuspenseWrapper>
        ),
      },
      {
        // CLA-1687 "Bring your client list". Gated inside the view; the URL
        // carries only the import's UUID.
        path: '/patients/import/:importId?',
        element: (
          <SuspenseWrapper>
            <PatientImportView />
          </SuspenseWrapper>
        ),
      },
      {
        path: '/patients/:uuid',
        element: (
          <SuspenseWrapper>
            <PatientDetail />
          </SuspenseWrapper>
        ),
      },
      {
        path: '/messaging',
        element: (
          <SuspenseWrapper>
            <MessagingView />
          </SuspenseWrapper>
        ),
      },
      {
        path: '/messaging/:conversationId',
        element: (
          <SuspenseWrapper>
            <MessagingView />
          </SuspenseWrapper>
        ),
      },
      {
        path: '/inbox',
        element: <SuspenseWrapper><InboxView /></SuspenseWrapper>,
      },
      {
        path: '/tasks',
        element: <SuspenseWrapper><TasksView /></SuspenseWrapper>,
      },
      {
        // CLA-1838 — the Help home: search, the page you came from, categories.
        path: '/help',
        element: <SuspenseWrapper><HelpPage /></SuspenseWrapper>,
      },
      {
        // CLA-1684, CLA-1838 — an article as a page (⌘K's "Open in Help",
        // the classifier's help results, related articles).
        path: '/help/:slug',
        element: <SuspenseWrapper><HelpArticlePage /></SuspenseWrapper>,
      },
      {
        path: '/reports',
        element: <SuspenseWrapper><PermissionGate require={REPORTS}><ReportsPage /></PermissionGate></SuspenseWrapper>,
      },
      {
        // CLA-821 — the "Review today" end-of-day ritual.
        path: '/tasks/review',
        element: <SuspenseWrapper><ReviewTodayPage /></SuspenseWrapper>,
      },
      {
        path: '/order',
        // CLA-980: the store is locked until the clinic is deliberately
        // unlocked. StoreGate covers every child route, the g-o shortcut,
        // and the command palette; the API enforces the same lock.
        element: <SuspenseWrapper><StoreGate><StoreLayout /></StoreGate></SuspenseWrapper>,
        children: [
          { index: true, element: <SuspenseWrapper><StoreHome /></SuspenseWrapper> },
          { path: 'browse', element: <SuspenseWrapper><StoreBrowse /></SuspenseWrapper> },
          { path: 'c/*', element: <SuspenseWrapper><CategoryPage /></SuspenseWrapper> },
          { path: 'search', element: <SuspenseWrapper><SearchResults /></SuspenseWrapper> },
          { path: 'product/:productId', element: <SuspenseWrapper><ProductDetailPage /></SuspenseWrapper> },
          { path: 'brand/:brandId', element: <SuspenseWrapper><BrandPage /></SuspenseWrapper> },
          { path: 'favourites', element: <SuspenseWrapper><FavouritesPage /></SuspenseWrapper> },
          { path: 'standing-orders', element: <SuspenseWrapper><StandingOrdersPage /></SuspenseWrapper> },
        ],
      },
      {
        path: '/settings/*',
        element: (
          <SuspenseWrapper>
            <SettingsView />
          </SuspenseWrapper>
        ),
      },
      {
        path: '/manage',
        element: <SuspenseWrapper><ManageLayout /></SuspenseWrapper>,
        children: [
          // Platform admin (superadmin)
          // Known-issue banner shown on every client (CLA-936). Platform-wide —
          // the text reaches every clinician on this deployment at once.
          { path: 'platform/feature-flags', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><FeatureFlagsAdmin /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/status', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><PlatformStatusAdmin /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/treatments', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><TreatmentsList /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/treatment-areas', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><TreatmentAreasList /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/types', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><ConsultationTypes /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/specialties', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><Specialties /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/labels', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><LabelCatalogue /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/consent-forms', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><ConsentFormsList /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/consent-forms/:id', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><ConsentFormDetail /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/medicines', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><MedicinesCatalogue /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/medicines/artg', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><ArtgWorklist /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/medicines/:id', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><MedicineDetail /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/pharma-companies', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><PharmaCompanies /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/pharma-companies/:id', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><PharmaCompanyDetail /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/distributors', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><DistributorsList /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/distributors/:id', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><DistributorDetail /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/prompts', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><PromptEditor /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/tokens', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><AiUsageDashboard /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/evaluations', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><EvalDashboard /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/comparisons', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><ComparisonsPanel /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/ai-routing', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><AiRoutingPanel /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/transcription', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><TranscriptionControlPanel /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/ai-models', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><AiModelManagement /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/users', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><UsersList /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/users/new', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><UserCreate /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/users/invite', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><UserInvite /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/pending-invites', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><PendingInvites /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/access-requests', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><AccessRequests /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/store-onboarding', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><StoreOnboardingQueue /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/subscriptions', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><Subscriptions /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/emails', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><AdminEmails /></PermissionGate></SuspenseWrapper> },
          // Support may open this one too: the server gates it on platform staff.
          { path: 'platform/help-library', element: <SuspenseWrapper><PlatformStaffGate><HelpLibrary /></PlatformStaffGate></SuspenseWrapper> },
          { path: 'platform/users/:id', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><UserDetail /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/adoption', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><Adoption /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/feedback', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><FeedbackConsole /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/feedback/:id', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><FeedbackCaseView /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/patients', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><AdminPatients /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/data', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><ComingSoon name="Data Operations" /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/data/duplicates', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><PatientDuplicates /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/data/import', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><PatientImport /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/settings', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><PlatformSettings /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/compliance', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><CompliancePlatformSettings /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/emr-sync', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><EmrSyncDashboard /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/danger', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><DangerZone /></PermissionGate></SuspenseWrapper> },
          { path: 'platform/api', element: <SuspenseWrapper><PermissionGate require={MANAGE_PLATFORM}><ApiManagement /></PermissionGate></SuspenseWrapper> },

          // Organisation (org admins)
          { path: 'org', element: <SuspenseWrapper><PermissionGate require={MANAGE_ORG}><OrgsList /></PermissionGate></SuspenseWrapper> },
          { path: 'org/:id', element: <SuspenseWrapper><PermissionGate require={MANAGE_ORG}><OrgDetail /></PermissionGate></SuspenseWrapper> },
          { path: 'org/clinics', element: <SuspenseWrapper><PermissionGate require={MANAGE_ORG}><OrgsList /></PermissionGate></SuspenseWrapper> },
          { path: 'org/clinics/:id', element: <SuspenseWrapper><PermissionGate require={MANAGE_ORG}><ClinicDetail /></PermissionGate></SuspenseWrapper> },
          { path: 'org/members', element: <SuspenseWrapper><PermissionGate require={MANAGE_ORG}><OrgMembers /></PermissionGate></SuspenseWrapper> },
          { path: 'org/settings', element: <SuspenseWrapper><PermissionGate require={MANAGE_ORG}><OrgSettingsPage /></PermissionGate></SuspenseWrapper> },

          // Network (network admins)
          { path: 'network/orgs', element: <SuspenseWrapper><PermissionGate require={MANAGE_NETWORK}><OrgsList /></PermissionGate></SuspenseWrapper> },
          { path: 'network/orders', element: <SuspenseWrapper><PermissionGate require={MANAGE_NETWORK}><NetworkOrders /></PermissionGate></SuspenseWrapper> },
          { path: 'network/orders/:orderId', element: <SuspenseWrapper><PermissionGate require={MANAGE_NETWORK}><NetworkOrderDetail /></PermissionGate></SuspenseWrapper> },
          { path: 'network/formulary', element: <SuspenseWrapper><PermissionGate require={MANAGE_NETWORK}><FormularyManager /></PermissionGate></SuspenseWrapper> },
          { path: 'network/credentials', element: <SuspenseWrapper><PermissionGate require={MANAGE_NETWORK}><CredentialsManager /></PermissionGate></SuspenseWrapper> },
          { path: 'network/compliance', element: <SuspenseWrapper><PermissionGate require={MANAGE_NETWORK}><ComplianceDashboard /></PermissionGate></SuspenseWrapper> },
          { path: 'network/pricing', element: <SuspenseWrapper><PermissionGate require={MANAGE_NETWORK}><WholesalePricingManager /></PermissionGate></SuspenseWrapper> },
          { path: 'network/accounts', element: <SuspenseWrapper><PermissionGate require={MANAGE_NETWORK}><WholesaleAccountsManager /></PermissionGate></SuspenseWrapper> },
          { path: 'network/settings', element: <SuspenseWrapper><PermissionGate require={MANAGE_NETWORK}><NetworkSettingsPage /></PermissionGate></SuspenseWrapper> },
          { path: 'network/invoices', element: <SuspenseWrapper><PermissionGate require={MANAGE_NETWORK}><InvoiceDashboard /></PermissionGate></SuspenseWrapper> },
          { path: 'network/invoices/:invoiceId', element: <SuspenseWrapper><PermissionGate require={MANAGE_NETWORK}><InvoiceDetail /></PermissionGate></SuspenseWrapper> },
          { path: 'network/payment-tiers', element: <SuspenseWrapper><PermissionGate require={MANAGE_NETWORK}><PaymentTierManager /></PermissionGate></SuspenseWrapper> },
          { path: 'network/payment-configs', element: <SuspenseWrapper><PermissionGate require={MANAGE_NETWORK}><ClinicPaymentConfig /></PermissionGate></SuspenseWrapper> },

          // Clinic (clinic admins — context from sidebar switcher)
          { path: 'clinic', element: <SuspenseWrapper><PermissionGate require={MANAGE_CLINIC}><ClinicOverview /></PermissionGate></SuspenseWrapper> },
          { path: 'clinic/staff', element: <SuspenseWrapper><PermissionGate require={MANAGE_CLINIC}><ClinicStaff /></PermissionGate></SuspenseWrapper> },
          { path: 'clinic/calendar', element: <SuspenseWrapper><PermissionGate require={MANAGE_CLINIC}><CalendarAdmin /></PermissionGate></SuspenseWrapper> },
          // CLA-2014 "Move from Acuity": services, patients, then Claro becomes the system of record.
          { path: 'clinic/calendar/move-from-acuity', element: <SuspenseWrapper><PermissionGate require={MANAGE_CLINIC}><AcuityMoveView /></PermissionGate></SuspenseWrapper> },
          { path: 'clinic/calendar/analytics', element: <SuspenseWrapper><PermissionGate require={MANAGE_CLINIC}><CalendarAnalytics /></PermissionGate></SuspenseWrapper> },
          { path: 'clinic/inventory', element: <SuspenseWrapper><PermissionGate require={MANAGE_CLINIC}><InventoryDashboard /></PermissionGate></SuspenseWrapper> },
          { path: 'clinic/inventory/intelligence', element: <SuspenseWrapper><PermissionGate require={MANAGE_CLINIC}><IntelligenceDashboard /></PermissionGate></SuspenseWrapper> },
          { path: 'clinic/inventory/locations', element: <SuspenseWrapper><PermissionGate require={MANAGE_CLINIC}><InventoryLocations /></PermissionGate></SuspenseWrapper> },
          { path: 'clinic/inventory/receive', element: <SuspenseWrapper><PermissionGate require={MANAGE_CLINIC}><ReceiveStock /></PermissionGate></SuspenseWrapper> },
          { path: 'clinic/inventory/transactions', element: <SuspenseWrapper><PermissionGate require={MANAGE_CLINIC}><TransactionHistory /></PermissionGate></SuspenseWrapper> },
          { path: 'clinic/inventory/batches/:batchId', element: <SuspenseWrapper><PermissionGate require={MANAGE_CLINIC}><BatchTrace /></PermissionGate></SuspenseWrapper> },
          { path: 'clinic/inventory/batches/:batchId/recall', element: <SuspenseWrapper><PermissionGate require={MANAGE_CLINIC}><BatchRecall /></PermissionGate></SuspenseWrapper> },
          { path: 'clinic/inventory/periods', element: <SuspenseWrapper><PermissionGate require={MANAGE_CLINIC}><InventoryPeriods /></PermissionGate></SuspenseWrapper> },
          { path: 'clinic/inventory/vials', element: <SuspenseWrapper><PermissionGate require={MANAGE_CLINIC}><VialTracker /></PermissionGate></SuspenseWrapper> },
          { path: 'clinic/inventory/expiry', element: <SuspenseWrapper><PermissionGate require={MANAGE_CLINIC}><ExpiryDashboard /></PermissionGate></SuspenseWrapper> },
          { path: 'clinic/inventory/stocktake', element: <SuspenseWrapper><PermissionGate require={MANAGE_CLINIC}><Stocktake /></PermissionGate></SuspenseWrapper> },
          { path: 'clinic/drug-register', element: <SuspenseWrapper><PermissionGate require={MANAGE_CLINIC}><DrugRegister /></PermissionGate></SuspenseWrapper> },
          { path: 'clinic/device-register', element: <SuspenseWrapper><PermissionGate require={MANAGE_CLINIC}><DeviceRegister /></PermissionGate></SuspenseWrapper> },
          { path: 'clinic/orders', element: <SuspenseWrapper><PermissionGate require={MANAGE_CLINIC}><ClinicOrders /></PermissionGate></SuspenseWrapper> },
          { path: 'clinic/orders/new', element: <SuspenseWrapper><PermissionGate require={MANAGE_CLINIC}><CreateOrder /></PermissionGate></SuspenseWrapper> },
          { path: 'clinic/orders/:orderId', element: <SuspenseWrapper><PermissionGate require={MANAGE_CLINIC}><OrderDetail /></PermissionGate></SuspenseWrapper> },
          { path: 'clinic/orders/:orderId/confirm-delivery', element: <SuspenseWrapper><PermissionGate require={MANAGE_CLINIC}><ConfirmDeliveryPage /></PermissionGate></SuspenseWrapper> },
          { path: 'clinic/packages', element: <SuspenseWrapper><PermissionGate require={MANAGE_CLINIC}><ClinicPackages /></PermissionGate></SuspenseWrapper> },
          { path: 'clinic/booking', element: <SuspenseWrapper><PermissionGate require={MANAGE_CLINIC}><ClinicBooking /></PermissionGate></SuspenseWrapper> },
          { path: 'clinic/sales', element: <SuspenseWrapper><PermissionGate require={['sales:read', ...MANAGE_CLINIC]}><ClinicSales /></PermissionGate></SuspenseWrapper> },
          // CLA-1949 — gift cards and promotional vouchers, beside the till.
          { path: 'clinic/gift-cards', element: <SuspenseWrapper><PermissionGate require={['sales:read', ...MANAGE_CLINIC]}><ClinicGiftCards /></PermissionGate></SuspenseWrapper> },
          { path: 'clinic/settings', element: <SuspenseWrapper><PermissionGate require={MANAGE_CLINIC}><ClinicSettingsPage /></PermissionGate></SuspenseWrapper> },
          { path: 'clinic/discarded-notes', element: <SuspenseWrapper><PermissionGate require={MANAGE_CLINIC}><ClinicDiscardedNotes /></PermissionGate></SuspenseWrapper> },
          { path: 'clinic/branding', element: <SuspenseWrapper><PermissionGate require={MANAGE_CLINIC}><ClinicBranding /></PermissionGate></SuspenseWrapper> },
          { path: 'clinic/messages', element: <SuspenseWrapper><PermissionGate require={MANAGE_CLINIC}><ClinicMessages /></PermissionGate></SuspenseWrapper> },
          { path: 'clinic/encounter-defaults', element: <SuspenseWrapper><PermissionGate require={MANAGE_CLINIC}><ClinicEncounterDefaults /></PermissionGate></SuspenseWrapper> },
          { path: 'clinic/consent-forms', element: <SuspenseWrapper><PermissionGate require={MANAGE_CLINIC}><ConsentFormsList /></PermissionGate></SuspenseWrapper> },
          { path: 'clinic/consent-forms/:id', element: <SuspenseWrapper><PermissionGate require={MANAGE_CLINIC}><ConsentFormDetail /></PermissionGate></SuspenseWrapper> },
          { path: 'clinic/payment-methods', element: <SuspenseWrapper><PermissionGate require={MANAGE_CLINIC}><PaymentMethodSetup /></PermissionGate></SuspenseWrapper> },
          { path: 'clinic/integrations', element: <SuspenseWrapper><PermissionGate require={MANAGE_CLINIC}><ClinicIntegrations /></PermissionGate></SuspenseWrapper> },
          // CLA-2008 — copying notes to Halaxy: the clinic's rule, health and sync log.
          { path: 'clinic/integrations/halaxy', element: <SuspenseWrapper><PermissionGate require={MANAGE_CLINIC}><ClinicHalaxySettings /></PermissionGate></SuspenseWrapper> },
          { path: 'clinic/integrations/halaxy/sync-log', element: <SuspenseWrapper><PermissionGate require={MANAGE_CLINIC}><ClinicHalaxySyncLog /></PermissionGate></SuspenseWrapper> },
          // CLA-1957 — the whole clinic as a ZIP. The emailed download link lands here; the server decides who may (owners and admins).
          { path: 'clinic/export', element: <SuspenseWrapper><PermissionGate require={MANAGE_CLINIC}><ClinicExport /></PermissionGate></SuspenseWrapper> },

          // Where Manage lands, by capability — NOT the platform console for
          // everyone, which is what this was (CLA-1159).
          { index: true, element: <SuspenseWrapper><ManageLanding /></SuspenseWrapper> },
        ],
      },
      {
        path: '/safety',
        element: <SuspenseWrapper><SafetyLayout /></SuspenseWrapper>,
        children: [
          { index: true, element: <SuspenseWrapper><IncidentsList /></SuspenseWrapper> },
          { path: 'incidents', element: <SuspenseWrapper><IncidentsList /></SuspenseWrapper> },
          { path: 'incidents/new', element: <SuspenseWrapper><IncidentReportForm /></SuspenseWrapper> },
          { path: 'incidents/:uuid', element: <SuspenseWrapper><IncidentDetail /></SuspenseWrapper> },
          { path: 'md-inbox', element: <SuspenseWrapper><MdInboxView /></SuspenseWrapper> },
          {
            path: 'complications/network',
            element: (
              <SuspenseWrapper>
                <PermissionGate require="incidents:read:network">
                  <NetworkRollupView />
                </PermissionGate>
              </SuspenseWrapper>
            ),
          },
          { path: 'regulator-reports', element: <SuspenseWrapper><RegulatorReportsList /></SuspenseWrapper> },
          { path: 'regulator-reports/:uuid', element: <SuspenseWrapper><RegulatorReportDetail /></SuspenseWrapper> },
          {
            path: 'auditors',
            element: (
              <SuspenseWrapper>
                <PermissionGate require={MANAGE_PLATFORM}>
                  <AuditorGrants />
                </PermissionGate>
              </SuspenseWrapper>
            ),
          },
        ],
      },
      {
        path: '*',
        element: (
          <SuspenseWrapper>
            <NotFound />
          </SuspenseWrapper>
        ),
      },
    ],
  },
  {
    path: '/forgot-password',
    element: <SuspenseWrapper><ForgotPasswordPage /></SuspenseWrapper>,
  },
  {
    path: '/reset-password',
    element: <SuspenseWrapper><ResetPasswordPage /></SuspenseWrapper>,
  },
  {
    // CLA-812 — public, gated self-serve signup. Top-level sibling (outside
    // AppShell) so unauthenticated visitors aren't bounced to login.
    path: '/signup',
    element: <SuspenseWrapper><SignupPage /></SuspenseWrapper>,
  },
  {
    path: '/consent/:token',
    element: <SuspenseWrapper><ConsentSigningPage /></SuspenseWrapper>,
  },
  {
    // Public, unauthenticated "Book now" embed (ADR-018 Phase B). Top-level
    // sibling (outside AppShell) so strangers aren't bounced to login. The
    // param is the clinic's slug, or its id for older links (CLA-1314).
    path: '/book/:clinicRef',
    element: <SuspenseWrapper><PublicBook /></SuspenseWrapper>,
  },
  {
    // The same page inside an iframe on the clinic's own website (CLA-1317):
    // no header chrome, and it tells the host page its height. The backend
    // serves this path without X-Frame-Options so it can be framed at all.
    path: '/embed/:clinicRef',
    element: <SuspenseWrapper><PublicBook embed /></SuspenseWrapper>,
  },
  {
    // A returning patient's "finish your booking" link (CLA-1316). Opening
    // it is what books; the token is the credential. Static `claim` segment
    // ranks above `/book/:clinicRef`.
    path: '/book/claim/:token',
    element: <SuspenseWrapper><PublicBookingClaim /></SuspenseWrapper>,
  },
  {
    // The patient's manage-booking link (CLA-1313) — the token in the
    // confirmation / reminder message is the credential, so no sign-in.
    path: '/booking/:token',
    element: <SuspenseWrapper><ManageBooking /></SuspenseWrapper>,
  },
  // ── Patient Portal ───────────────────────────────────────────────
  {
    // The same handoff page at the URL claro-elixir #1475 puts in the QR code
    // today (`/p/<clinic slug>/h/<token>`), so a code made by that backend
    // opens it too.
    path: '/p/:slug/h/:token',
    element: (
      <SuspenseWrapper>
        <PortalBrandingProvider>
          <SuspenseWrapper><PortalHandoff /></SuspenseWrapper>
        </PortalBrandingProvider>
      </SuspenseWrapper>
    ),
  },
  {
    // "Your visit" (ADR-035 §4, CLA-1629): where a handoff lands — the
    // backend's `next: /p/<clinic slug>/visit/<note uuid>`. Signed in, so
    // inside PortalLayout (which sends a signed-out patient to sign in with
    // `?next=` back here, the clinic carried); PortalSlugClinic names the
    // clinic from the slug when the URL has no `?clinic_id`.
    path: '/p/:slug/visit/:noteUuid',
    element: <SuspenseWrapper><PortalBrandingProvider /></SuspenseWrapper>,
    children: [
      {
        element: <SuspenseWrapper><PortalSlugClinic /></SuspenseWrapper>,
        children: [
          {
            element: <SuspenseWrapper><PortalLayout /></SuspenseWrapper>,
            children: [{ index: true, element: <SuspenseWrapper><PatientVisit /></SuspenseWrapper> }],
          },
        ],
      },
    ],
  },
  {
    path: '/portal',
    element: <SuspenseWrapper><PortalBrandingProvider /></SuspenseWrapper>,
    children: [
      { path: 'login', element: <SuspenseWrapper><PortalLogin /></SuspenseWrapper> },
      { path: 'register', element: <SuspenseWrapper><PortalRegister /></SuspenseWrapper> },
      // Landing pages for the portal emails (CLA-1618): the clinic's invite
      // and the patient's sign-in link. Signed out, so outside PortalLayout.
      { path: 'activate', element: <SuspenseWrapper><PortalActivate /></SuspenseWrapper> },
      { path: 'auth/verify', element: <SuspenseWrapper><PortalMagicLinkVerify /></SuspenseWrapper> },
      // The QR code off the clinician's screen (ADR-035 Phase 1, 2026-10-08).
      { path: 'handoff/:token', element: <SuspenseWrapper><PortalHandoff /></SuspenseWrapper> },
      {
        element: <SuspenseWrapper><PortalLayout /></SuspenseWrapper>,
        children: [
          // The portal opens on the patient's care, not the shop (John,
          // 2026-10-08): sign-in, PORTAL_HOME and the old links all land here.
          { index: true, element: <PortalCareHome /> },
          { path: 'shop', element: <SuspenseWrapper><PatientStoreHome /></SuspenseWrapper> },
          { path: 'product/:id', element: <SuspenseWrapper><PatientProductDetail /></SuspenseWrapper> },
          { path: 'cart', element: <SuspenseWrapper><PatientCart /></SuspenseWrapper> },
          { path: 'checkout', element: <SuspenseWrapper><PortalCheckout /></SuspenseWrapper> },
          { path: 'orders', element: <SuspenseWrapper><PatientOrders /></SuspenseWrapper> },
          { path: 'orders/:uuid', element: <SuspenseWrapper><PatientOrderDetail /></SuspenseWrapper> },
          { path: 'book', element: <SuspenseWrapper><PatientBook /></SuspenseWrapper> },
          { path: 'bookings', element: <SuspenseWrapper><PatientBookings /></SuspenseWrapper> },
          { path: 'profile', element: <SuspenseWrapper><PatientProfile /></SuspenseWrapper> },
          { path: 'documents', element: <SuspenseWrapper><PatientDocuments /></SuspenseWrapper> },
          { path: 'plan', element: <SuspenseWrapper><PatientPlan /></SuspenseWrapper> },
          // CLA-1949 — the patient's gift cards, adding one by code, a balance check, and buying one.
          { path: 'gift-cards', element: <SuspenseWrapper><PatientGiftCards /></SuspenseWrapper> },
          { path: 'gift-cards/buy', element: <SuspenseWrapper><PatientGiftCardBuy /></SuspenseWrapper> },
        ],
      },
    ],
  },
]

/**
 * A clinic's own booking host, `<slug>.clarohealth.app` (CLA-2010): the Book
 * page and its embed, for the clinic the host names, and nothing else of the
 * app. Any other path goes to the page (the Worker sends other app pages to
 * claro.clinic before they get here).
 */
export const bookingHostRoutes: RouteObject[] = [
  {
    path: '/',
    element: <SuspenseWrapper><PublicBook /></SuspenseWrapper>,
    errorElement: <RouteErrorFallback />,
  },
  {
    path: '/embed',
    element: <SuspenseWrapper><PublicBook embed /></SuspenseWrapper>,
    errorElement: <RouteErrorFallback />,
  },
  { path: '*', element: <BookingHostHome /> },
]

/** Anything else on a booking host → its page, keeping `?service=` and the rest. */
function BookingHostHome() {
  const { search } = useLocation()
  return <Navigate to={`/${search}`} replace />
}

export const router = createBrowserRouter(onBookingHost() ? bookingHostRoutes : appRoutes)
